import { ListFilter, SortDirection } from "@common/api/filterTypes";
import { DateFieldValue } from "@common/components/filterPopover/subMenues/DateFieldSubMenu";
import { NumberIntervalValue } from "@common/components/filterPopover/subMenues/NumberFieldSubMenu";
import { EntityType } from "@common/Common.types";
import { FrameworkControlStatus } from "../../framework/frameworks/Frameworks.types";
import { RiskLevelIndicator } from "../../riskSettings/riskArea/riskAreaDialog/riskLevels/riskLevelIndicators";
import { RiskMitigationPlanStatus } from "../riskMitigationStatus";
import { RiskExposureMode } from "../riskExposure/RiskExposure.types";

export { RiskMitigationPlanStatus } from "../riskMitigationStatus";

// The open/active states — the default-visible set (Completed + Cancelled hidden by default; Promoted
// is excluded — it is not surfaced until the promotion flow ships).
export const openMitigationStatuses: Array<RiskMitigationPlanStatus> = [
    RiskMitigationPlanStatus.Proposed,
    RiskMitigationPlanStatus.Approved,
    RiskMitigationPlanStatus.Planned,
    RiskMitigationPlanStatus.InProgress,
    RiskMitigationPlanStatus.Deferred,
];

export const allMitigationStatuses: Array<RiskMitigationPlanStatus> = [
    RiskMitigationPlanStatus.Proposed,
    RiskMitigationPlanStatus.Approved,
    RiskMitigationPlanStatus.Planned,
    RiskMitigationPlanStatus.InProgress,
    RiskMitigationPlanStatus.Completed,
    RiskMitigationPlanStatus.Deferred,
    RiskMitigationPlanStatus.Cancelled,
    RiskMitigationPlanStatus.Promoted,
];

// Table/display column ids (used for the table `property` and headers).
export enum RiskMitigationColumn {
    Name = "name",
    Action = "action",
    Type = "type",
    Entity = "entity",
    Scenario = "scenario",
    CurrentRisk = "currentRisk",
    ExpectedRisk = "expectedRisk",
    Responsible = "responsible",
    Deadline = "deadline",
    CreatedDate = "createdDate",
    Status = "status",
    EstimatedCost = "estimatedCost",
    EstimatedLoss = "estimatedLoss",
    ExposureAfterMitigation = "exposureAfterMitigation",
    ControlCount = "controlCount",
    ControlStatus = "controlStatus",
}

// Mirrors the backend RiskMitigationColumn enum (numeric) — used in the search request payload.
export enum RiskMitigationBackendColumn {
    Action = 0,
    Entity = 1,
    CurrentRisk = 2,
    Responsible = 3,
    Deadline = 4,
    Status = 5,
    Scenario = 6,
    CreatedDate = 7,
    ExpectedRisk = 8,
    Name = 9,
    EstimatedCost = 10,
    EstimatedLoss = 11,
    ExposureAfterMitigation = 12,
    ControlCount = 13,
    ControlStatus = 14,
    Type = 15,
}

export const mitigationColumnToBackend: Record<RiskMitigationColumn, RiskMitigationBackendColumn> = {
    [RiskMitigationColumn.Name]: RiskMitigationBackendColumn.Name,
    [RiskMitigationColumn.Action]: RiskMitigationBackendColumn.Action,
    [RiskMitigationColumn.Type]: RiskMitigationBackendColumn.Type,
    [RiskMitigationColumn.Entity]: RiskMitigationBackendColumn.Entity,
    [RiskMitigationColumn.Scenario]: RiskMitigationBackendColumn.Scenario,
    [RiskMitigationColumn.CurrentRisk]: RiskMitigationBackendColumn.CurrentRisk,
    [RiskMitigationColumn.ExpectedRisk]: RiskMitigationBackendColumn.ExpectedRisk,
    [RiskMitigationColumn.Responsible]: RiskMitigationBackendColumn.Responsible,
    [RiskMitigationColumn.Deadline]: RiskMitigationBackendColumn.Deadline,
    [RiskMitigationColumn.CreatedDate]: RiskMitigationBackendColumn.CreatedDate,
    [RiskMitigationColumn.Status]: RiskMitigationBackendColumn.Status,
    [RiskMitigationColumn.EstimatedCost]: RiskMitigationBackendColumn.EstimatedCost,
    [RiskMitigationColumn.EstimatedLoss]: RiskMitigationBackendColumn.EstimatedLoss,
    [RiskMitigationColumn.ExposureAfterMitigation]: RiskMitigationBackendColumn.ExposureAfterMitigation,
    [RiskMitigationColumn.ControlCount]: RiskMitigationBackendColumn.ControlCount,
    [RiskMitigationColumn.ControlStatus]: RiskMitigationBackendColumn.ControlStatus,
};

export const backendColumnToRisk = Object.fromEntries(Object.entries(mitigationColumnToBackend).map(([column, backend]) => [backend, column])) as Record<
    RiskMitigationBackendColumn,
    RiskMitigationColumn
>;

// Every selectable column, in display order — Created before Deadline (work item #6691). Not the default view:
// the columns shown on first load come from the backend system-default view
// (RiskMitigationSavedViewStrategy.GetDefaultColumns), and this list only says which persisted column ids are
// still valid, so a column added here alone would be selectable but never shown by default.
export const allRiskMitigationColumns: Array<RiskMitigationColumn> = [
    RiskMitigationColumn.Name,
    RiskMitigationColumn.Action,
    RiskMitigationColumn.Type,
    RiskMitigationColumn.Entity,
    RiskMitigationColumn.Scenario,
    RiskMitigationColumn.CurrentRisk,
    RiskMitigationColumn.ExpectedRisk,
    RiskMitigationColumn.Responsible,
    RiskMitigationColumn.CreatedDate,
    RiskMitigationColumn.Deadline,
    RiskMitigationColumn.Status,
    RiskMitigationColumn.EstimatedCost,
    RiskMitigationColumn.EstimatedLoss,
    RiskMitigationColumn.ExposureAfterMitigation,
    RiskMitigationColumn.ControlCount,
    RiskMitigationColumn.ControlStatus,
];

export interface RiskMitigationFilterRequest {
    riskAreaId: string;
    showOnlyMine?: boolean;
    statuses?: ListFilter<RiskMitigationPlanStatus>;
    responsibleIds?: ListFilter<string>;
    // The two halves of the Type filter: which entity types are in scope, and whether the organisation's own
    // plans are. Undefined on the second is no opinion, which leaves them in.
    types?: ListFilter<EntityType>;
    includeOrganisationSubject?: boolean;
    entityIds?: ListFilter<string>;
    scenarioIds?: ListFilter<string>;
    // 1-based levels on the area scale: the assessment's current risk and the plan's expected (after-mitigation)
    // risk, the two chips the table renders.
    riskLevels?: ListFilter<number>;
    expectedRiskLevels?: ListFilter<number>;
    createdFrom?: string;
    createdTo?: string;
    estimatedCostFrom?: number;
    estimatedCostTo?: number;
    estimatedLossFrom?: number;
    estimatedLossTo?: number;
    exposureAfterMitigationFrom?: number;
    exposureAfterMitigationTo?: number;
    // Ja/Nej: the plan itself is open and past its deadline, unrelated to its controls.
    hasOverdueDeadline?: boolean;
    // Ja/Nej: the plan itself links at least one framework control. Plan-scoped like every other control filter
    // here; Nej is what the governance overdue tile's "plans without control coverage" figure links to.
    hasLinkedControls?: boolean;
    // Plan-scoped control filters (the plan's own RiskMitigationPlanControl scope, never head coverage),
    // mirroring the operational overview's control filters.
    frameworkControlIds?: ListFilter<string>;
    controlStatuses?: ListFilter<number>;
    searchText?: string;
    pageNumber: number;
    pageSize: number;
    sortByColumn: RiskMitigationBackendColumn;
    sortDirection: SortDirection;
}

// The persisted Saved-View filter shape (mirrors the request). Deliberately omits riskAreaId (from the route),
// searchText (ad-hoc), and pageSize (constant default). The created-date range is kept as a DateFieldValue so
// the filter bar can rebuild its selection; it is converted to createdFrom/createdTo when building the request.
export interface RiskMitigationSavedFilter {
    showOnlyMine?: boolean;
    statuses?: ListFilter<RiskMitigationPlanStatus>;
    responsibleIds?: ListFilter<string>;
    // The Type filter's raw selection, exactly as the governance overview persists it: the entity types ticked,
    // and separately whether the organisation option is. The shape the backend takes is derived on the way out.
    types?: ListFilter<EntityType>;
    includeOrganisation?: boolean;
    entityIds?: ListFilter<string>;
    scenarioIds?: ListFilter<string>;
    riskLevels?: ListFilter<number>;
    expectedRiskLevels?: ListFilter<number>;
    createdDateRange?: DateFieldValue | null;
    estimatedCostRange?: NumberIntervalValue;
    estimatedLossRange?: NumberIntervalValue;
    exposureAfterMitigationRange?: NumberIntervalValue;
    hasOverdueDeadline?: boolean;
    hasLinkedControls?: boolean;
    frameworkControlIds?: ListFilter<string>;
    controlStatuses?: ListFilter<number>;
    pageNumber: number;
    sortByColumn: RiskMitigationBackendColumn;
    sortDirection: SortDirection;
}

// No status filter by default so every status stays visible on initial load — a completed plan must not
// appear to vanish. Mirrors the backend strategy's GetDefaultFilterData. Users can still filter by status.
export const defaultRiskMitigationFilter: RiskMitigationSavedFilter = {
    showOnlyMine: false,
    pageNumber: 1,
    sortByColumn: RiskMitigationBackendColumn.CreatedDate,
    sortDirection: SortDirection.DESC,
};

export interface RiskMitigationExportHeader {
    header: string;
    column: string;
}

// The risk level on the area scale; carries scale + label so the chip colours match the matrix.
export interface RiskMitigationLevel {
    level: number;
    label: string;
    scale: number;
}

export interface RiskMitigationItem {
    planId: string;
    riskAssessmentId: string;
    name: string;
    description: string;
    // All three are null when the plan treats an organisation-level risk, which has no entity as its subject.
    entityType: EntityType | null;
    entityId: string | null;
    entityName: string | null;
    scenarioName: string;
    createdDate: string;
    status: RiskMitigationPlanStatus;
    responsibleId: string | null;
    responsibleName: string | null;
    deadline: string | null;
    currentRisk: RiskMitigationLevel | null;
    expectedRisk: RiskMitigationLevel | null;
    // The assessment was re-scored after this open plan was authored, so the expected/after-mitigation figure
    // no longer reflects the current baseline — drives the drift warning triangle on that column.
    isDrifted: boolean;
    // The board task materialised from this plan has been reported done. Completing the task auto-completes the
    // plan, so on a Completed plan this drives a non-blocking nudge on the status to apply the mitigation.
    linkedTaskCompleted: boolean;
    // The plan's stamped estimated cost + expected (after-mitigation) exposure, and the assessment's stamped
    // gross exposure (estimated loss). All gated by the backend to the current exposure configuration, null
    // otherwise. Formatted against the result's exposure mode/currency/bound.
    estimatedCost: number | null;
    grossExposure: number | null;
    expectedExposure: number | null;
    // The plan's OWN controls (round scope), never head coverage. The status is the backend's task-derived
    // roll-up (any Overdue wins); null when the plan links no controls.
    linkedControlIds: Array<string>;
    linkedControlStatus: FrameworkControlStatus | null;
}

export interface RiskMitigationKpis {
    proposed: number;
    approved: number;
    planned: number;
    inProgress: number;
    completed: number;
    deferred: number;
    cancelled: number;
    promoted: number;
}

export interface RiskMitigationSearchResult {
    items: Array<RiskMitigationItem>;
    totalCount: number;
    totalPages: number;
    kpis: RiskMitigationKpis;
    // The area's configured risk-level indicators (index 0 = level 1), so the chips colour-match the
    // operational/assessment views' indicator palette.
    riskLevelIndicators: Array<RiskLevelIndicator>;
    // The area's risk-level labels (index 0 = level 1), so the risk and after-mitigation filters offer the same
    // options the chips show.
    riskLevels: Array<string>;
    // The customer's exposure configuration, so the exposure columns format without a second query. None mode
    // hides the columns. Mirrors the operational search result.
    exposureMode: RiskExposureMode;
    exposureCurrencyCode: string | null;
    exposureScaleUpperBound: number | null;
}
